import { Alignment, Icon, Radio, RadioGroup, Switch } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import * as React from 'react';
import { Tooltip } from 'vet-bones/components';

import {
  DEFAULT_PROJECT_PROPERTY_TYPE,
  ProjectPropertyType,
} from 'src/constants/project';
import { TEST_IDS } from 'src/constants/testIds';
import * as copy from 'src/templates/copy';
import { useFlushedDebouncedCallback } from 'src/utils/hooks/useFlushedDebouncedCallback';

export const PROPERTY_TYPE_CHECKED = ProjectPropertyType.RDFS;
export const PROPERTY_TYPE_UNCHECKED = ProjectPropertyType.SCHEMA_ORG;

export type PropertyTypeSelectorProps = {
  allowCustom?: boolean;
  debounced?: boolean;
  propertyType?: ProjectPropertyType;
  setPropertyType: (propertyType: ProjectPropertyType) => void;
};

export const PropertyTypeSelector: React.VFC<PropertyTypeSelectorProps> = ({
  allowCustom = false,
  debounced = false,
  propertyType = DEFAULT_PROJECT_PROPERTY_TYPE,
  setPropertyType,
}) => {
  const debouncedSetPropertyType = useFlushedDebouncedCallback(
    setPropertyType,
    1000
  );
  const handleSetPropertyType = debounced
    ? debouncedSetPropertyType
    : setPropertyType;

  const label = (
    <>
      {copy.components.project.propertyType.label}{' '}
      <Tooltip content={copy.components.project.propertyType.info}>
        <Icon icon={IconNames.INFO_SIGN} />
      </Tooltip>{' '}
    </>
  );

  const control = allowCustom ? (
    <RadioGroup
      className="sd-drawer-project-settings__radio-group"
      inline
      label={label}
      onChange={({ currentTarget: { value } }) =>
        handleSetPropertyType(value as ProjectPropertyType)
      }
      selectedValue={propertyType}
    >
      {(Object.keys(ProjectPropertyType) as ProjectPropertyType[]).map(
        (type) => (
          <Radio
            key={type}
            label={copy.components.project.propertyType.buttonText(type)}
            value={type}
          />
        )
      )}
    </RadioGroup>
  ) : (
    <Switch
      key={propertyType} // keep uncontrolled input in sync
      alignIndicator={Alignment.RIGHT}
      defaultChecked={propertyType === ProjectPropertyType.RDFS}
      inline
      labelElement={label}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
        handleSetPropertyType(
          event.target.checked ? PROPERTY_TYPE_CHECKED : PROPERTY_TYPE_UNCHECKED
        )
      }
    />
  );

  return (
    <div
      className="sd-property-type-selector"
      data-testid={TEST_IDS.propertyType.selector}
    >
      {control}
    </div>
  );
};
